// =============================================================================
// ui/PendingDeletionBanner.tsx — "your account is counting down".
// -----------------------------------------------------------------------------
// ── 🚨 WHY THIS EXISTS AT ALL ──
//
// A scheduled deletion is invisible. The app looks completely normal — every
// trade, every screenshot, every setting is exactly where it was — and thirty
// days later it is all gone. Nothing else in the product can be true and
// unnoticeable in that way, so it gets a permanent surface.
//
// ── ⚠ A BANNER, NOT A MODAL, AND THAT IS A DELIBERATE CHANGE ──
//
// The old client opens a MODAL at sign-in with "Leave it scheduled" and "Restore
// my account". Dismiss it and the warning is gone until the next sign-in — which
// for someone who stays signed in for days can be the whole grace window. The
// modal also arrives at the worst moment for reading: the instant the app opens.
//
// A banner cannot be dismissed into nothing. It states the deadline, it carries
// Restore, and it stays until the deletion is actually cancelled.
//
// ⚠ IT RENDERS NOTHING while the check is in flight or if the check FAILS. A
// banner that flickers on every load would be ignored within a day, and one that
// guessed would be worse — the Danger Zone card is where a failed check is
// reported, because that is where someone goes to ask the question.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from './state/supabase';
import {
  loadPendingDeletion, restoreAccount, type PendingDeletion,
} from '../io/sync/accountDeletion';
import { daysUntilPurge, formatPurgeDate } from '../selectors/accountDeletion';

export function PendingDeletionBanner() {
  const { sb, userId } = useSupabase();
  const [pending, setPending] = useState<PendingDeletion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    try {
      setPending(await loadPendingDeletion(sb));
    } catch {
      // ⚠ Silent. See the header: this surface says nothing rather than
      // something uncertain, and the Danger Zone reports the failed read.
      setPending(null);
    }
  }, [sb]);

  useEffect(() => { void check(); }, [check]);

  if (!pending) return null;

  const when = formatPurgeDate(pending.requestedAt);
  const left = daysUntilPurge(pending.requestedAt, new Date());

  const restore = async () => {
    setBusy(true);
    setError(null);
    const res = await restoreAccount(sb, userId);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    setPending(null);
  };

  return (
    <div className="dz-banner" role="alert" data-testid="pending-deletion-banner">
      <div className="grow">
        <b>
          This account is scheduled for deletion
          {when ? ` on ${when}` : ''}
          {left === null ? '' : left === 0 ? ' — today' : ` — ${left} day${left === 1 ? '' : 's'} left`}.
        </b>{' '}
        <span className="muted">
          Nothing has been removed yet. Restoring cancels it completely.
        </span>
        {error ? <div className="red">{error}</div> : null}
      </div>
      <button type="button" className="btn primary" disabled={busy} onClick={() => void restore()}>
        {busy ? 'Restoring…' : 'Restore my account'}
      </button>
    </div>
  );
}
